import type { Memory } from '@ralph/contracts'
import clsx from 'clsx'
import { Bot, Brain, Globe, Lightbulb, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MemoryRow } from '@/api/memories.ts'
import {
  agentNameOf,
  filterMemories,
  filterToValue,
  type MemoryFilter,
  memoryGroupOptions,
  withFilterOption
} from '@/components/memory/memory-grouping.ts'
import { Badge, Button, EmptyState, IconButton, Input, Panel, Select } from '@/components/ui/primitives.tsx'
import {
  useConfirmMemory,
  useCreateMemoryRow,
  useDeleteMemoryRow,
  useMemoryRows,
  useUpdateMemoryRow
} from '@/hooks/use-memories.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Personal memory (AGM-090…093).
 *
 * Every entry is inspectable, editable and removable by the person it describes — which is
 * the point. Phase 4 added the two distinctions that make that true rather than nominal:
 *
 *  - a preference is labeled with WHERE it applies. Memory is partitioned per agent, so a
 *    page that showed one flat list would imply everything follows the reader everywhere,
 *    which is exactly the leak the partition prevents;
 *  - a suggested entry is confirmed, not merely enabled. The badge has shipped for a while
 *    with nothing behind it; the confirm action is what makes "inert until you agree" a
 *    thing the user can actually do, and it is deliberately a separate control from the
 *    on/off checkbox next to it.
 *
 * The page reads as a ledger — one glass panel, hairline-separated rows, every measurement in
 * tabular mono — because a list of statements a machine holds about you is something you
 * audit, not something you browse.
 *
 * // WIRE: rows carry `agentId` + `agentDisplayName` from `GET /api/memories`, and confirm
 * // posts to `/api/memories/:id/confirm` — see `api/memories.ts` for the envelope note. An
 * // agent that has been retired keeps its label here (AGM-093); the API is the only place
 * // that knows it is retired, so nothing on this page needs to change when it happens.
 */
export const MemoryPage = () => {
  // Inside the component body, never at module scope: a capture at import freezes the locale
  // and re-renders in the previous language with nothing to show for it (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.memory
  const memories = useMemoryRows()
  const create = useCreateMemoryRow()
  const update = useUpdateMemoryRow()
  const remove = useDeleteMemoryRow()

  const [statement, setStatement] = useState('')
  const [category, setCategory] = useState<Memory['category']>('output_preference')

  /*
   * `?agent=<id>` is how the chat surface links here: an answer that disclosed "shaped by
   * your saved preferences" leads to the preferences THAT agent uses, not to a list of
   * everything (AGM-092). The URL is the state so the filtered view can be linked to,
   * bookmarked and reloaded — a filter that lived only in component state would drop back to
   * everything the moment someone refreshed the page they had just been sent to.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const agentParam = searchParams.get('agent')
  const filter: MemoryFilter = agentParam
    ? { agentId: agentParam }
    : searchParams.get('show') === 'global'
      ? 'global'
      : 'all'

  const changeFilter = (next: MemoryFilter) => {
    const params = new URLSearchParams(searchParams)
    params.delete('agent')
    params.delete('show')
    if (typeof next === 'object') params.set('agent', next.agentId)
    else if (next === 'global') params.set('show', 'global')
    // `replace` so paging through filters does not fill the Back button with them.
    setSearchParams(params, { replace: true })
  }

  const items = memories.data?.items ?? []
  const options = withFilterOption(
    memoryGroupOptions(fmt, items, { all: t.filterAll, everywhere: t.filterGlobal }),
    filter,
    agentParam ?? ''
  )
  const visible = filterMemories(items, filter)
  const activeValue = filterToValue(filter)

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[880px] px-4 pb-[34px] pt-1 sm:px-8 lg:px-10">
        <header className="pb-5 pt-4">
          <h1 className="text-[30px] sm:text-[38px]">{t.title}</h1>
          <p className="mt-[7px] max-w-[470px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{t.lead}</p>
        </header>

        <Panel className="px-4 py-3.5">
          <h2 className="eyebrow">{t.whereTitle}</h2>
          {/* The same two sentences the listing page discloses, in the same order and with the
              same glyphs the rows below use — a reader who checks one against the other has to
              find one story about memory, not two. */}
          <ul className="mt-2 divide-y divide-rule">
            <li className="flex items-start gap-2.5 pb-2.5">
              <Globe size={13} className="mt-[3px] shrink-0 text-fg-subtle" aria-hidden />
              <span className="text-[13px] leading-relaxed text-fg-muted">{t.whereGlobal}</span>
            </li>
            <li className="flex items-start gap-2.5 pt-2.5">
              <Bot size={13} className="mt-[3px] shrink-0 text-fg-subtle" aria-hidden />
              <span className="text-[13px] leading-relaxed text-fg-muted">{t.whereAgent}</span>
            </li>
          </ul>
        </Panel>

        <form
          className="mt-4 flex flex-col gap-2 sm:flex-row"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!statement.trim()) return
            await create.mutateAsync({ category, statement: statement.trim() })
            setStatement('')
          }}
        >
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as Memory['category'])}
            aria-label={t.addCategoryLabel}
            className="sm:w-[196px] sm:shrink-0"
          >
            {(Object.keys(t.categories) as Memory['category'][]).map((value) => (
              <option key={value} value={value}>
                {t.categories[value]}
              </option>
            ))}
          </Select>
          <Input
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder={t.addPlaceholder}
            aria-label={t.addStatementLabel}
            className="min-w-0 flex-1"
          />
          <Button type="submit" variant="primary" icon={<Plus size={13} aria-hidden />} disabled={create.isPending}>
            {t.add}
          </Button>
        </form>
        {/* Said next to the field, not in a tooltip: anything typed here is global, and a
            person adding "call me Sam" deserves to know it reaches every agent. */}
        <p className="mt-2 text-[12.5px] text-fg-subtle">{t.addHint}</p>

        {/* Shown whenever a filter is active, even with one group: arriving from a chat link
            with the control hidden would leave a filtered page and no way back to the rest. */}
        {(options.length > 2 || filter !== 'all') && (
          <fieldset className="mt-5">
            {/* A real fieldset/legend rather than a labelled div: these pills are one control
                with several settings, and the legend is what says so to a screen reader
                without having to invent an id to point at. */}
            <legend className="eyebrow mb-1.5">{t.filterLabel}</legend>
            <div className="flex flex-wrap items-center gap-1.5">
              {options.map((option) => {
                const value = filterToValue(option.filter)
                const active = value === activeValue
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => changeFilter(option.filter)}
                    aria-pressed={active}
                    /* The count is set apart in mono, so the spoken name is assembled from the
                       catalog's whole phrase rather than from two adjacent fragments. */
                    aria-label={t.filterCount(fmt, option.label, option.count)}
                    className={clsx(
                      'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-[12px] tracking-[-0.01em]',
                      'transition-[color,background-color,border-color,box-shadow] duration-200',
                      active
                        ? 'border-rule-strong bg-card-solid font-semibold text-fg shadow-glass'
                        : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                    )}
                  >
                    {option.label}
                    <span className="readout text-[9px] text-fg-subtle">{fmt.formatCount(option.count)}</span>
                  </button>
                )
              })}
            </div>
          </fieldset>
        )}

        <div className="mt-5">
          {items.length === 0 ? (
            <Panel className="px-4">
              <EmptyState title={t.empty.title} body={t.empty.body} />
            </Panel>
          ) : visible.length === 0 ? (
            <Panel className="px-4">
              <EmptyState title={t.emptyFiltered.title} body={t.emptyFiltered.body} />
            </Panel>
          ) : (
            <Panel className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-rule px-4 py-3">
                <Brain size={12} className="shrink-0 text-fg-muted" aria-hidden />
                <h2 className="eyebrow">{t.listTitle}</h2>
                <span className="readout ml-auto text-fg-muted">{t.listCount(fmt, visible.length)}</span>
              </div>
              <ul className="divide-y divide-rule">
                {visible.map((memory, index) => (
                  <li
                    key={memory.id}
                    className="rise"
                    // Capped: a long list should not still be arriving seconds after it loaded.
                    style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                  >
                    <MemoryEntry
                      memory={memory}
                      onToggle={(enabled) => update.mutate({ id: memory.id, enabled })}
                      onForget={() => remove.mutate(memory.id)}
                    />
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>
    </div>
  )
}

const MemoryEntry = ({
  memory,
  onToggle,
  onForget
}: {
  memory: MemoryRow
  onToggle: (enabled: boolean) => void
  onForget: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.memory
  const confirm = useConfirmMemory()
  const agentName = agentNameOf(memory)
  const suggested = memory.source === 'proposed'

  return (
    <div className="flex items-start gap-2.5 px-3 py-3 transition-colors duration-200 hover:bg-card-solid sm:px-4">
      {/* The checkbox is 15px because that is the scale of the design, but the label around it
          is a 26px target: on a phone this switch is the only way to stop using a preference. */}
      <label className="-ml-0.5 mt-px flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center">
        <input
          type="checkbox"
          checked={memory.enabled}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={memory.enabled ? t.disable : t.enable}
          className="h-[15px] w-[15px] cursor-pointer accent-primary"
        />
      </label>

      <div className="min-w-0 flex-1">
        <p className={clsx('text-[14px] leading-snug', memory.enabled ? 'text-fg' : 'text-fg-subtle line-through')}>
          {memory.statement}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* Where it applies comes first: it changes what the sentence above means. */}
          {agentName === null ? (
            <Badge>
              <Globe size={10} className="shrink-0" aria-hidden />
              {t.everywhere}
            </Badge>
          ) : (
            <Badge>
              <Bot size={10} className="shrink-0" aria-hidden />
              {t.agentOnly(agentName)}
            </Badge>
          )}
          <Badge>{t.categories[memory.category]}</Badge>
          {suggested && (
            <Badge tone="primary">
              <Lightbulb size={10} className="shrink-0" aria-hidden />
              {t.suggested}
            </Badge>
          )}
          <span className="readout text-fg-subtle">
            {t.added(fmt.formatAgo(memory.createdAt))}
            {memory.useCount > 0 && ` · ${t.used(fmt, memory.useCount)}`}
          </span>
        </div>

        {suggested && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="primary" disabled={confirm.isPending} onClick={() => confirm.mutate(memory.id)}>
              {confirm.isPending ? t.confirming : t.confirm}
            </Button>
            <span className="max-w-[430px] text-[12.5px] leading-snug text-fg-subtle">{t.confirmHint}</span>
            {confirm.isError && (
              <span role="alert" className="text-[12.5px] text-alert">
                {t.confirmFailed}
              </span>
            )}
          </div>
        )}
      </div>

      <IconButton label={t.forget} tone="alert" onClick={onForget}>
        <Trash2 size={13} aria-hidden />
      </IconButton>
    </div>
  )
}
